import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Analytics } from "../../api";
import { fmtMoney } from "../../lib/format";
import { InfoTooltip } from "../info-tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { EmptyState } from "../ui/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

/**
 * Xodimlar samaradorligi — 5 ta ustun.
 *
 * Ilgari 8 ta ustun bor edi (ish haqi, ehtiyot qismlar, jami, mijozlar…) va
 * jadval 1280px noutbukda sig'masdi. Ish haqi/ehtiyot qismlar/mijozlar
 * ustunlari OLIB TASHLANDI — backend ularni baribir qaytaradi (ta'riflari
 * o'zgarmagan), shunchaki bu yerda chizilmaydi.
 *
 * "Daromad" = `total_revenue` (ish haqi + ishga biriktirilgan tovarlar);
 * `share_pct` ham serverda aynan shu jami bo'yicha hisoblanadi, ya'ni ustun
 * bilan ulush bir xil asosda.
 */
export function EmployeesCard({ rows }: { rows: Analytics["employees"] }) {
  const { t } = useTranslation("analytics");
  const totals = useMemo(() => {
    const works = rows.reduce((s, e) => s + e.works_count, 0);
    const total = rows.reduce((s, e) => s + parseFloat(e.total_revenue), 0);
    return { works, total, avg: works ? total / works : 0 };
  }, [rows]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t("employees.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rows.length === 0 ? (
          <EmptyState title={t("empty.noWorks")} className="py-8" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("employees.columns.employee")}</TableHead>
                <TableHead className="text-right">{t("employees.columns.works")}</TableHead>
                <TableHead className="text-right">
                  {t("employees.columns.revenue")}{" "}
                  <InfoTooltip text={t("employees.hints.revenue")} align="end" />
                </TableHead>
                <TableHead className="text-right">
                  {t("employees.columns.avgCheck")}{" "}
                  <InfoTooltip text={t("employees.hints.avgCheck")} align="end" />
                </TableHead>
                <TableHead className="text-right">
                  {t("employees.columns.share")}{" "}
                  <InfoTooltip text={t("employees.hints.share")} align="end" />
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.user_id}>
                  <TableCell className="font-medium">{e.full_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{e.works_count}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">
                    {fmtMoney(e.total_revenue)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {fmtMoney(e.avg_check)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {e.share_pct.toFixed(1)}%
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell>{t("employees.totals")}</TableCell>
                <TableCell className="text-right tabular-nums">{totals.works}</TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {fmtMoney(totals.total)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{fmtMoney(totals.avg)}</TableCell>
                <TableCell className="text-right tabular-nums">100%</TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
